import { metricValue, tierDocsOf, type FleetMetricEnvelope, type McpQuotaAll } from "../../../api/mcp";
import { Bars } from "../../../components/charts";
import { ResourceView } from "../../../components/states";
import { Badge, Card } from "../../../components/ui";
import { Button, Divider, Subheading } from "../../../f7";
import type { Resource } from "../../../hooks/useResource";
import { ProductTag } from "../../../products/PerProduct";

/**
 * How many tiers set neither a `tool_default` nor a daily total.
 *
 * Both read absent as unlimited, as the server does. `tool_default` is the
 * document field — the old `caps` map is gone, and reading it left this at zero.
 */
function CreditPosture({ quota }: { quota: McpQuotaAll }) {
  const docs = tierDocsOf(quota);
  const tiers = Object.keys(docs).sort();
  const uncapped = tiers.filter(
    (t) => (docs[t]?.tool_default ?? -1) < 0 && (docs[t]?.max_calls_per_day ?? -1) < 0,
  );
  return (
    <>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl/8 font-semibold tabular-nums text-slate-950 dark:text-white">
          {uncapped.length}
        </span>
        <span className="text-xs/5 text-slate-500 dark:text-slate-400">
          of {tiers.length} tiers uncapped
        </span>
      </div>
      <ul className="mt-3 flex flex-wrap gap-1.5">
        {uncapped.map((t) => (
          <li key={t}>
            <Badge tone="amber">{t}</Badge>
          </li>
        ))}
      </ul>
    </>
  );
}
/**
 * Tier distribution and credit posture — one panel, the way the design draws
 * it, split by a Divider rather than by a card boundary.
 *
 * A FAILED FETCH IS NOT A SLOW ONE. This used to take the resolved value and
 * render `Loading…` whenever it was absent, so a 403 or a dead route showed
 * a spinner that never resolved — the operator waits, then reloads, then
 * files a bug against the wrong thing. Each state now says which it is.
 */
export function TierAndCredits({
  state,
  onRetry,
  quota,
  onQuotaRetry,
}: {
  state: Resource<FleetMetricEnvelope<"tier_distribution">>;
  onRetry: () => void;
  quota: Resource<McpQuotaAll>;
  onQuotaRetry: () => void;
}) {
  const distribution = metricValue(state);
  const total = distribution
    ? Object.values(distribution).reduce((n, v) => n + v, 0)
    : null;

  return (
    <Card
      title="Tenants by tier"
      note="MCP entitlements · configured records only"
      aside={
        <>
          {total !== null && (
            <span className="font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
              {total} measured
            </span>
          )}
          <ProductTag id="mcp" />
        </>
      }
    >
      <ResourceView state={state} what="the tier distribution" onRetry={onRetry}>
        {() =>
          distribution && (
            <>
              <Bars
                rows={Object.entries(distribution)
                  .sort(([a], [b]) => (a === "none" ? 1 : b === "none" ? -1 : a.localeCompare(b)))
                  .map(([tier, count]) => ({
                    label: tier === "none" ? "no entitlements row" : tier,
                    value: count,
                    note: `of ${total}`,
                    // "none" is not a tier — it is merchants the server treats as
                    // free without a stored row, a different fact worth its own tone.
                    tone: tier === "none" ? ("mute" as const) : ("brand" as const),
                  }))}
                max={total ?? 1}
              />
              {/* The design's axis under the bars: without it the longest bar reads
                  as "full" rather than as a count against the fleet. */}
              <div className="mt-2 flex justify-between font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                <span>0</span>
                <span>{total}</span>
              </div>
            </>
          )
        }
      </ResourceView>

      <Divider className="my-4" />

      <Subheading>Credit posture</Subheading>
      {quota.kind === "ready" ? (
        <CreditPosture quota={quota.data} />
      ) : (
        <ResourceView state={quota} what="the quota documents" onRetry={onQuotaRetry} />
      )}
      {/* An outline Button, as the design draws it. A text link with an arrow
          was the one call-to-action on this page that did not look like one. */}
      <div className="mt-4">
        <Button outline href="/policy/tiers">
          Tiers & limits
        </Button>
      </div>
    </Card>
  );
}
